import React, { useContext, useEffect, useRef } from 'react';
import { ReferralCodeContext } from './ReferralCodeContext';

type SmartLinkContextType = {
    isSmartLinkEnabled: boolean;
};

const SmartLinkContext = React.createContext<SmartLinkContextType>({
    isSmartLinkEnabled: false,
});

interface SmartLinkProviderProps {
    languageCodename: string;
    children: React.ReactNode;
}

interface UpdateMessageData {
    projectId: string;
    variant: { id: string; codename: string };
    item: { id: string; codename: string };
    elements: ReadonlyArray<{
        element: { id: string; codename: string };
        type: string;
        data: { value: unknown };
    }>;
}

const HEADING_FONT_SIZES: Record<string, string> = {
    h1: 'var(--heading-large)',
    h2: 'var(--heading-medium)',
    h3: 'var(--heading-small)',
    h4: 'var(--subheading-large)',
    h5: 'var(--subheading-medium)',
    h6: 'var(--subheading-small)',
    p: '1rem',
};

const HEADING_FONT_FAMILIES: Record<string, string> = {
    h1: 'var(--font-avenir)',
    h2: 'var(--font-avenir)',
    h3: 'var(--subheading-family)',
    h4: 'var(--subheading-family)',
    h5: 'var(--subheading-family)',
    h6: 'var(--subheading-family)',
    p: 'inherit',
};

const VALID_HEADING_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'div'];

const TAG_FIELD_TARGETS: Record<string, string> = {
    description_tag: 'description_copy',
    display_headline_tag: 'display_headline',
    display_sub_headline_tag: 'display_sub_headline',
    headline_tag: 'headline',
};

const STYLE_FIELD_TARGETS: Record<string, string> = {
    description_style: 'description_copy',
    sub_headline_style: 'display_sub_headline',
    headline_style: 'headline',
};

const STYLE_VALUE_MAP: Record<string, string> = {
    large: 'h3',
    medium: 'h4',
    small: 'h5',
};

function findHeadingElement(container: Element): Element | null {
    if (/^H[1-6]$/i.test(container.tagName) || container.tagName === 'P') {
        return container;
    }
    return container.querySelector('h1,h2,h3,h4,h5,h6,p');
}

function replaceElementTag(target: Element, newTag: string): void {
    if (target.tagName.toLowerCase() === newTag) return;
    const newEl = document.createElement(newTag);
    for (const attr of Array.from(target.attributes)) {
        newEl.setAttribute(attr.name, attr.value);
    }
    newEl.innerHTML = target.innerHTML;
    (newEl as HTMLElement).style.cssText = (
        target as HTMLElement
    ).style.cssText;
    target.parentNode?.replaceChild(newEl, target);
}

/**
 * Collects elements matching a codename, searching both descendants of the
 * container AND the container itself.  The Headline component puts both
 * data-kontent-item-id and data-kontent-element-codename on the same
 * element, so querySelectorAll alone would miss it.
 */
function collectFieldElements(container: Element, codename: string): Element[] {
    const elements: Element[] = Array.from(
        container.querySelectorAll(
            `[data-kontent-element-codename="${codename}"]`
        )
    );
    if (container.getAttribute('data-kontent-element-codename') === codename) {
        elements.push(container);
    }
    return elements;
}

function applyTextTemplate(element: Element, value: string): string {
    const current = element.getAttribute('data-kontent-template-current');
    const total = element.getAttribute('data-kontent-template-total');
    return value
        .replace('{current}', current ?? '{current}')
        .replace('{total}', total ?? '{total}');
}

/**
 * When a field element receives non-empty content, walk up the DOM and
 * unhide any ancestor that was hidden with `display: none` (used for
 * conditionally rendered fields that are empty at build time).
 * When content is cleared, re-hide the nearest hidden-capable wrapper.
 */
function syncVisibility(
    el: Element,
    container: Element,
    hasContent: boolean
): void {
    let parent = el.parentElement;
    while (parent && parent !== container.parentElement) {
        const htmlParent = parent as HTMLElement;
        if (hasContent) {
            if (htmlParent.style.display === 'none') {
                htmlParent.style.display = '';
            }
        } else {
            // Re-hide the closest wrapper that directly contains the field
            if (parent.contains(el) && parent !== container) {
                htmlParent.style.display = 'none';
                break;
            }
        }
        parent = parent.parentElement;
    }
}

function applyContentPosition(container: Element, position: string): void {
    const headings = container.querySelectorAll(
        '[data-kontent-element-codename]'
    );
    if (headings.length === 0) return;
    const contentWrapper = headings[0].parentElement?.parentElement;
    if (!contentWrapper) return;

    const el = contentWrapper as HTMLElement;
    el.style.position = '';
    el.style.left = '';
    el.style.transform = '';
    el.style.maxWidth = '';
    el.style.width = '';
    el.style.margin = '';
    el.style.textAlign = '';

    if (position === 'right') {
        el.style.position = 'absolute';
        el.style.left = '50%';
        el.style.transform = 'translateY(-50%)';
        el.style.maxWidth = '740px';
        el.style.width = '48%';
    } else if (position === 'center') {
        el.style.margin = '0 auto';
        el.style.textAlign = 'center';
    }
}

const GLOBAL_MASTHEAD_COPY_SELECTOR =
    '[data-global-masthead-vertical-align-desktop]';

function applyVerticalAlignment(
    container: Element,
    alignment: string,
    codename?: string
): void {
    const globalMastheadOverlay = container.querySelector(
        GLOBAL_MASTHEAD_COPY_SELECTOR
    ) as HTMLElement | null;
    if (globalMastheadOverlay) {
        const isDesktopField = codename === 'vertical_alignment_desktop';
        const isMobileField = codename === 'vertical_alignment_mobile';
        if (isDesktopField) {
            globalMastheadOverlay.dataset.globalMastheadVerticalAlignDesktop =
                alignment;
        } else if (isMobileField) {
            globalMastheadOverlay.dataset.globalMastheadVerticalAlignMobile =
                alignment;
        }

        return;
    }

    const headings = container.querySelectorAll(
        '[data-kontent-element-codename]'
    );
    if (headings.length === 0) return;
    const inner = headings[0].parentElement?.parentElement;
    if (!inner) return;

    const el = inner as HTMLElement;
    el.style.alignSelf =
        alignment === 'top'
            ? 'flex-start'
            : alignment === 'bottom'
            ? 'flex-end'
            : alignment === 'center'
            ? 'center'
            : '';
    el.style.justifyContent = '';
}

/**
 * Patches the DOM in-place when Kontent.ai sends an Update event.
 *
 * Handles:
 *  - text / rich_text → textContent / innerHTML
 *  - asset → img src swap
 *  - multiple_choice for alignment → textAlign
 *  - multiple_choice for tag / *_tag → HTML element replacement
 *  - multiple_choice for style / *_style → font-size + font-family
 *  - multiple_choice for content_position / product_name_position → layout
 *  - multiple_choice for vertical_alignment → vertical positioning
 *  - number → textContent
 *
 * Also manages visibility: fields rendered with display:none when empty
 * at build time are automatically shown/hidden as content changes.
 */
export function patchDomWithUpdate(
    data: UpdateMessageData,
    prepareAnchor?: (anchor: HTMLAnchorElement) => void
): void {
    if (!data?.item?.id || !data?.elements) return;

    const itemId = data.item.id;
    const itemContainers = document.querySelectorAll(
        `[data-kontent-item-id="${itemId}"]`
    );
    if (itemContainers.length === 0) return;

    for (const element of data.elements) {
        const codename = element.element?.codename;
        if (!codename) continue;

        for (const container of itemContainers) {
            const fieldElements = collectFieldElements(container, codename);

            switch (element.type) {
                case 'text':
                case 'url_slug':
                case 'number': {
                    const val = String(element.data?.value ?? '');
                    for (const el of fieldElements) {
                        const numberSymbol = el.getAttribute(
                            'data-kontent-number-symbol'
                        );
                        const displayValue =
                            element.type === 'number' && numberSymbol
                                ? numberSymbol.repeat(
                                      Math.max(0, Number(val) || 0)
                                  )
                                : applyTextTemplate(el, val);
                        const targetAttribute = el.getAttribute(
                            'data-kontent-target-attribute'
                        );
                        const cssVariable = el.getAttribute(
                            'data-kontent-css-variable'
                        );
                        if (cssVariable) {
                            const assessmentRoot = el.closest(
                                '[data-infrared-assessment-root]'
                            ) as HTMLElement | null;
                            assessmentRoot?.style.setProperty(
                                cssVariable,
                                displayValue
                            );
                        } else if (targetAttribute) {
                            el.setAttribute(targetAttribute, displayValue);
                            if (
                                targetAttribute === 'href' &&
                                el instanceof HTMLAnchorElement
                            ) {
                                prepareAnchor?.(el);
                            }
                        } else {
                            el.textContent = displayValue;
                        }
                        syncVisibility(el, container, !!displayValue);
                    }
                    break;
                }

                case 'rich_text': {
                    const html = (element.data?.value as string) ?? '';
                    for (const el of fieldElements) {
                        el.innerHTML = html;
                        // Prepare only the updated fragment, without observing
                        // or rescanning the document for unrelated changes.
                        if (prepareAnchor) {
                            el.querySelectorAll<HTMLAnchorElement>(
                                'a[href]'
                            ).forEach(prepareAnchor);
                        }
                        syncVisibility(el, container, !!html);
                    }
                    break;
                }

                case 'asset': {
                    if (!Array.isArray(element.data?.value)) break;
                    const assets = element.data.value as Array<{
                        url: string;
                        description?: string;
                        name?: string;
                    }>;
                    if (assets.length === 0) break;
                    for (const el of fieldElements) {
                        const img =
                            el.tagName === 'IMG'
                                ? (el as HTMLImageElement)
                                : el.querySelector('img');
                        if (img) {
                            (img as HTMLImageElement).src = assets[0].url;
                            (img as HTMLImageElement).srcset = '';
                            const alt =
                                assets[0].description || assets[0].name || '';
                            if (alt) (img as HTMLImageElement).alt = alt;
                        }
                    }
                    break;
                }

                case 'multiple_choice': {
                    if (!Array.isArray(element.data?.value)) break;
                    const choices = element.data.value as Array<{
                        codename: string;
                    }>;
                    if (choices.length === 0) break;
                    const val = choices[0].codename;

                    if (
                        codename === 'alignment' ||
                        codename === 'content_alignment'
                    ) {
                        const heading = findHeadingElement(container);
                        if (heading) {
                            (heading as HTMLElement).style.textAlign =
                                val || '';
                        }
                        (container as HTMLElement).style.textAlign = val || '';
                    }

                    if (codename === 'tag') {
                        const target = findHeadingElement(container);
                        if (target && VALID_HEADING_TAGS.includes(val)) {
                            replaceElementTag(target, val);
                        }
                    }

                    if (codename === 'style') {
                        const fontSize = HEADING_FONT_SIZES[val];
                        if (fontSize) {
                            const target = findHeadingElement(container);
                            if (target) {
                                (target as HTMLElement).style.fontSize =
                                    fontSize;
                            }
                        }
                    }

                    if (TAG_FIELD_TARGETS[codename]) {
                        const targetCodename = TAG_FIELD_TARGETS[codename];
                        const targets = collectFieldElements(
                            container,
                            targetCodename
                        );
                        for (const t of targets) {
                            if (VALID_HEADING_TAGS.includes(val)) {
                                replaceElementTag(t, val);
                            }
                        }
                    }

                    if (STYLE_FIELD_TARGETS[codename]) {
                        const targetCodename = STYLE_FIELD_TARGETS[codename];
                        const mappedTag = STYLE_VALUE_MAP[val] || val;
                        const fontSize = HEADING_FONT_SIZES[mappedTag];
                        const fontFamily = HEADING_FONT_FAMILIES[mappedTag];
                        const targets = collectFieldElements(
                            container,
                            targetCodename
                        );
                        for (const t of targets) {
                            if (fontSize) {
                                (t as HTMLElement).style.fontSize = fontSize;
                            }
                            if (fontFamily) {
                                (t as HTMLElement).style.fontFamily =
                                    fontFamily;
                            }
                        }
                    }

                    if (
                        codename === 'content_position' ||
                        codename === 'product_name_position'
                    ) {
                        applyContentPosition(container, val);
                    }

                    if (
                        codename === 'vertical_alignment' ||
                        codename === 'vertical_alignment_desktop' ||
                        codename === 'vertical_alignment_mobile'
                    ) {
                        applyVerticalAlignment(container, val, codename);
                    }

                    if (codename === 'masthead_style') {
                        const section =
                            container.closest('section') || container;
                        const el = section as HTMLElement;
                        if (val === 'short') {
                            el.style.height = '321px';
                            el.style.marginTop = '96px';
                        } else {
                            el.style.height = '100vh';
                            el.style.marginTop = '';
                        }
                    }

                    if (codename === 'hide_description_on_mobile') {
                        const descEls = container.querySelectorAll(
                            '[data-kontent-element-codename="description_copy"]'
                        );
                        for (const descEl of descEls) {
                            const wrapper =
                                descEl.closest('[class]')?.parentElement;
                            if (wrapper) {
                                (wrapper as HTMLElement).dataset.hideMobile =
                                    val === 'yes' ? 'true' : 'false';
                            }
                        }
                    }
                    break;
                }

                default:
                    break;
            }
        }
    }
}

/**
 * Initializes the Kontent.ai Smart Link SDK for Live Preview and Live Edit.
 *
 * `languageCodename` MUST be the raw `preferred_language` value from the
 * GraphQL query (e.g. "AU-en", "GB-en", "default") — never the normalized
 * lowercase version from `normalizeLocale()`, and never `pageContext.treelang`
 * which is remapped to "default" on AU/NZ builds.
 */
const SmartLinkProvider: React.FC<SmartLinkProviderProps> = ({
    languageCodename,
    children,
}) => {
    const instanceRef = useRef<any>(null);
    const { prepareAnchor } = useContext(ReferralCodeContext);
    const prepareAnchorRef = useRef(prepareAnchor);
    prepareAnchorRef.current = prepareAnchor;
    const isEnabled = process.env.GATSBY_ENABLE_SMART_LINK === 'TRUE';

    useEffect(() => {
        if (!isEnabled || typeof window === 'undefined') return;

        let destroyed = false;

        import('@kontent-ai/smart-link').then(
            ({ default: KontentSmartLink, KontentSmartLinkEvent }) => {
                if (destroyed) return;

                const instance = KontentSmartLink.initialize({
                    defaultDataAttributes: {
                        environmentId:
                            process.env.GATSBY_KONTENT_ENVIRONMENT_ID || '',
                        languageCodename: languageCodename,
                    },
                    queryParam: '',
                });

                instance.on(
                    KontentSmartLinkEvent.Refresh,
                    (
                        _data: any,
                        _metadata: any,
                        _originalRefresh: () => void
                    ) => {
                        // Intentionally suppressed: calling originalRefresh()
                        // reloads the iframe which reverts all DOM patches made
                        // by the Update handler.  Content editors can manually
                        // refresh the preview when they need the latest
                        // published build.
                    }
                );

                instance.on(KontentSmartLinkEvent.Update, ((
                    data: UpdateMessageData
                ) =>
                    patchDomWithUpdate(data, prepareAnchorRef.current)) as any);

                instanceRef.current = instance;
            }
        );

        return () => {
            destroyed = true;
            instanceRef.current?.destroy();
            instanceRef.current = null;
        };
    }, [isEnabled, languageCodename]);

    return (
        <SmartLinkContext.Provider value={{ isSmartLinkEnabled: isEnabled }}>
            {children}
        </SmartLinkContext.Provider>
    );
};

export { SmartLinkContext, SmartLinkProvider };
